import React from 'react';

// BOM module icons. Item-kind icons share one tile size so the layout is stable
// when an icon swaps after first expand.

// Item-kind icons (BOM, Assembly, Part, Document, ECO): one design everywhere
// in the BOM module — thin purple outline (assembly bolder) on a soft lavender
// tile. Product structures tree, Part Catalog, DMS, ECOs and BOMs lists.
const TILE = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
  borderRadius: 7, background: '#F4F1FE',
};
const KIND_ART = {
  bom: <><circle cx="12" cy="5" r="2.2" /><circle cx="5.5" cy="18.5" r="2" /><circle cx="12" cy="18.5" r="2" /><circle cx="18.5" cy="18.5" r="2" /><path d="M12 7.2v4.3M5.5 16.5v-2.5h13v2.5M12 11.5v5" /></>,
  asm: <><path d="M12 2.8l8 4.4v9.6l-8 4.4-8-4.4V7.2z" /><path d="M4 7.2l8 4.4 8-4.4M12 11.6v9.6" /><path d="M8 5l8 4.4" opacity=".6" /></>,
  part: <><path d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z" /><circle cx="12" cy="12" r="3.2" /></>,
  doc: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M8.5 12.5h7M8.5 16h5" /></>,
  eco: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M15 13.2a3.2 3.2 0 1 0-.9 2.9" /><path d="M15.4 10.8v2.6h-2.6" /></>,
  // Software BOM work items
  release: <><path d="M12 3l8 4.5-8 4.5-8-4.5z" /><path d="M4 12l8 4.5 8-4.5M4 16.5L12 21l8-4.5" /></>,
  component: <><path d="M9 4h6v4h-6zM4 16h6v4H4zM14 16h6v4h-6z" /><path d="M12 8v4M7 16v-2h10v2" /></>,
  vuln: <><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" /><path d="M12 8.5v4.5M12 16.2v.1" /></>,
  // Pages that are not a work item list
  dash: <><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="4.5" rx="1.5" /><rect x="13" y="10.5" width="7" height="9.5" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /></>,
  tree: <><rect x="9" y="3" width="6" height="4.5" rx="1" /><rect x="3" y="16.5" width="6" height="4.5" rx="1" /><rect x="15" y="16.5" width="6" height="4.5" rx="1" /><path d="M12 7.5v4.5M6 16.5V12h12v4.5" /></>,
  usage: <><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5M8 10.5h5M10.5 8v5" /></>,
  baseline: <><path d="M12 3l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" /></>,
  branch: <><circle cx="6" cy="5" r="2" /><circle cx="6" cy="19" r="2" /><circle cx="18" cy="7" r="2" /><path d="M6 7v10M18 9c0 5-6 4-11.5 8" /></>,
  import: <><path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" /><path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" /></>,
  ai: <><path d="M11 3.5l1.8 4.7 4.7 1.8-4.7 1.8L11 16.5l-1.8-4.7L4.5 10l4.7-1.8z" /><path d="M18 14.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" /></>,
  unknown: <rect x="5" y="5" width="14" height="14" rx="2.5" strokeDasharray="2.5 2.5" />,
};
const KIND_COLOR = { bom: '#5B3FD0', asm: '#4B32C3', part: '#7C6AE0', doc: '#8E7FE3', eco: '#6A4FD8', unknown: '#B9B0EA' };
// The assembly is drawn bolder so it stands out from its parts.
const KIND_STROKE = { asm: 2.2 };
export const KIND_LABEL = {
  bom: 'BOM', asm: 'Assembly', part: 'Part', doc: 'Document (DMS)', eco: 'ECO', unknown: 'Not checked yet',
  release: 'Software release', component: 'Software component', vuln: 'Vulnerability',
};

export function KindIcon({ kind, size = 24, title }) {
  const label = title === undefined ? KIND_LABEL[kind] : title;
  return (
    <span style={{ ...TILE, width: size, height: size, color: KIND_COLOR[kind] || '#7C6AE0' }}
      title={label || undefined} aria-label={label || undefined} role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : 'true'}>
      <svg width={Math.round(size * 0.7)} height={Math.round(size * 0.7)} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={KIND_STROKE[kind] || 1.5} strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false">{KIND_ART[kind]}</svg>
    </span>
  );
}

export const BomIcon = (p) => <KindIcon kind="bom" {...p} />;
export const AssemblyIcon = (p) => <KindIcon kind="asm" {...p} />;
export const PartIcon = (p) => <KindIcon kind="part" {...p} />;
export const DocumentIcon = (p) => <KindIcon kind="doc" {...p} />;
export const EcoIcon = (p) => <KindIcon kind="eco" {...p} />;

export function ChevronIcon({ open, size = 12 }) {
  return (
    <svg
      viewBox="0 0 12 12"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{
        transform: open ? 'rotate(90deg)' : 'rotate(0)',
        transition: 'transform 150ms ease',
      }}
    >
      <path d="M4 2 L8 6 L4 10" fill="none"
            stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// Where Used — magnifier over a small hierarchy. Click-to-find-where-it's-used.
export function WhereUsedIcon({ size = 14 }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="6.5" cy="6.5" r="3.6"
              fill="none" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="9.4" y1="9.4" x2="13" y2="13"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="4.8" y1="6.5" x2="8.2" y2="6.5"
            stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="6.5" y1="4.8" x2="6.5" y2="8.2"
            stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

// Locate — a target/crosshair. Used in Where-Used results to expand a BOM
// and scroll to the matching descendant.
export function LocateIcon({ size = 14 }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="8" cy="8" r="5"
              fill="none" stroke="currentColor" strokeWidth="1.4"/>
      <circle cx="8" cy="8" r="1.6" fill="currentColor"/>
      <line x1="8" y1="1.4" x2="8" y2="3.4"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="8" y1="12.6" x2="8" y2="14.6"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="1.4" y1="8" x2="3.4" y2="8"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="12.6" y1="8" x2="14.6" y2="8"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

// A row whose children haven't been checked yet; replaced with Assembly or Part once known.
export const UnknownChildIcon = (p) => <KindIcon kind="unknown" {...p} />;
